import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import type { paths } from "@/types/api";

type CreateBody =
  paths["/api/v1/inspections"]["post"]["requestBody"]["content"]["application/json"];

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export const inspectionKeys = {
  all: ["inspections"] as const,
  byContract: (c: string) => ["inspections", "contract", c] as const,
  detail: (id: string) => ["inspections", "detail", id] as const,
  photos: (id: string) => ["inspections", id, "photos"] as const,
};

export function useInspections(contractId?: string, opts?: { enabled?: boolean }) {
  return useQuery({
    enabled: opts?.enabled ?? true,
    queryKey: contractId ? inspectionKeys.byContract(contractId) : inspectionKeys.all,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/inspections", {
        params: { query: contractId ? { contract_id: contractId } : {} },
      });
      if (error || !data) throw new Error("Failed to load inspections");
      return data;
    },
  });
}

export function useInspection(id: string | undefined) {
  return useQuery({
    queryKey: inspectionKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/inspections/{inspection_id}", {
        params: { path: { inspection_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load inspection");
      return data;
    },
  });
}

export function useCreateInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.POST("/api/v1/inspections", {
        body: body as CreateBody,
      });
      if (error || !data) throw new Error("Failed to create inspection");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inspectionKeys.all }),
  });
}

function useMutateInspection(id: string) {
  const qc = useQueryClient();
  return (fn: () => Promise<unknown>) =>
    fn().then((r) => {
      qc.invalidateQueries({ queryKey: inspectionKeys.detail(id) });
      qc.invalidateQueries({ queryKey: inspectionKeys.all });
      return r;
    });
}

export function useUpdateItems(id: string) {
  const run = useMutateInspection(id);
  return useMutation({
    mutationFn: (items: Array<Record<string, unknown>>) =>
      run(async () => {
        const { data, error } = await apiClient.PATCH(
          "/api/v1/inspections/{inspection_id}/items",
          { params: { path: { inspection_id: id } }, body: { items } as never },
        );
        if (error) throw new Error("Failed to update items");
        return data;
      }),
  });
}

export function useUpdateInspection(id: string) {
  const run = useMutateInspection(id);
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      run(async () => {
        const { error } = await apiClient.PATCH(
          "/api/v1/inspections/{inspection_id}",
          { params: { path: { inspection_id: id } }, body: body as never },
        );
        if (error) throw new Error("Failed to update inspection");
      }),
  });
}

export function useFinalizeInspection(id: string) {
  const run = useMutateInspection(id);
  return useMutation({
    mutationFn: () =>
      run(async () => {
        const { error } = await apiClient.POST(
          "/api/v1/inspections/{inspection_id}/finalize",
          { params: { path: { inspection_id: id } } },
        );
        if (error) throw new Error("Failed to finalize");
      }),
  });
}

export function useSendInspectionForSigning(id: string) {
  const run = useMutateInspection(id);
  return useMutation({
    mutationFn: () =>
      run(async () => {
        const { data, error } = await apiClient.POST(
          "/api/v1/inspections/{inspection_id}/send-for-signing",
          { params: { path: { inspection_id: id } } },
        );
        if (error)
          throw new Error(
            (error as { detail?: string })?.detail ?? "Failed to send for signing",
          );
        return data;
      }),
  });
}

export function useGenerateDeductions(id: string) {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST(
        "/api/v1/inspections/{inspection_id}/generate-deductions",
        { params: { path: { inspection_id: id } } },
      );
      if (error || !data) throw new Error("Could not generate deductions");
      return data;
    },
  });
}

export function useInspectionPhotos(id: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: inspectionKeys.photos(id),
    enabled: (opts?.enabled ?? true) && Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/inspections/{inspection_id}/photos",
        { params: { path: { inspection_id: id } } },
      );
      if (error || !data) throw new Error("Failed to load photos");
      return data;
    },
  });
}

export function useUploadInspectionPhoto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { file: File; roomId?: string }) => {
      const form = new FormData();
      form.append("file", vars.file);
      if (vars.roomId) form.append("room_id", vars.roomId);
      const token = useAuthStore.getState().token;
      const res = await fetch(
        `${BASE_URL}/api/v1/inspections/${id}/photos`,
        {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
        },
      );
      if (!res.ok) throw new Error("Upload failed");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inspectionKeys.photos(id) }),
  });
}

export function useDeleteInspectionPhoto(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (photoId: string) => {
      const { error } = await apiClient.DELETE(
        "/api/v1/inspections/{inspection_id}/photos/{photo_id}",
        { params: { path: { inspection_id: id, photo_id: photoId } } },
      );
      if (error)
        throw new Error(
          (error as { detail?: string })?.detail ?? "Couldn't delete photo",
        );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: inspectionKeys.photos(id) }),
  });
}
